import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, User, Menu, X } from 'lucide-react';
import { BRAND_NAME } from '../brand';
import { useAuth } from '../hooks/useAuth';
import { useCart } from '../hooks/useCart';

function ScrollToTop(): null {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export default function SiteLayout(): JSX.Element {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileNavRef = useRef<HTMLElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  const itemCount = cart?.itemCount ?? 0;

  const onLogout = async (): Promise<void> => {
    await logout();
    setMobileMenuOpen(false);
    navigate('/');
  };

  const closeMenu = useCallback((): void => setMobileMenuOpen(false), []);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const menu = mobileNavRef.current;
    const hamburger = hamburgerRef.current;
    if (!menu) return;

    const getFocusable = (): HTMLElement[] => {
      const els = Array.from(
        menu.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')
      );
      return hamburger ? [hamburger, ...els] : els;
    };

    const focusable = getFocusable();
    if (focusable.length > 1) focusable[1].focus();

    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        hamburger?.focus();
        return;
      }
      if (e.key !== 'Tab') return;
      const elements = getFocusable();
      if (elements.length === 0) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileMenuOpen]);

  return (
    <div className="min-h-full flex flex-col">
      <ScrollToTop />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-black focus:text-white focus:px-4 focus:py-2 focus:text-sm"
      >
        Skip to content
      </a>
      <header className="border-b border-stone-200 bg-white sticky top-0 z-10">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="font-semibold text-lg text-black py-2" onClick={closeMenu}>
            {BRAND_NAME}
          </Link>

          <div className="flex items-center gap-3 sm:hidden">
            <Link to="/cart" className="relative inline-flex items-center p-3 -m-3 hover:text-black" aria-label={itemCount > 0 ? `Cart (${itemCount} item${itemCount === 1 ? '' : 's'})` : 'Cart'}>
              <ShoppingCart className="size-5" aria-hidden="true" />
              {itemCount > 0 && (
                <span className="absolute -top-1.5 -right-2 inline-block rounded-full bg-black text-white text-[10px] leading-none px-1.5 py-0.5" aria-hidden="true">
                  {itemCount}
                </span>
              )}
            </Link>
            <button
              ref={hamburgerRef}
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-3 -mr-3 text-stone-700 hover:text-black"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            </button>
          </div>

          <nav className="hidden sm:flex flex-wrap items-center justify-end gap-x-2 gap-y-1 text-sm text-stone-700">
            <Link to="/browse" className="px-2 py-1.5 hover:text-black">Browse</Link>
            <Link to="/sell" className="px-2 py-1.5 hover:text-black">Sell</Link>
            <Link to="/about" className="px-2 py-1.5 hover:text-black">About</Link>
            {user ? (
              <>
                {user.sellerProfileExists ? (
                  <Link to="/seller/dashboard" className="px-2 py-1.5 hover:text-black">Seller dashboard</Link>
                ) : null}
                {user.role === 'ADMIN' ? (
                  <Link to="/admin/moderation" className="px-2 py-1.5 hover:text-black">Admin</Link>
                ) : null}
                <Link to="/account" className="px-2 py-1.5 hover:text-black inline-flex items-center gap-1">
                  <User className="size-4" aria-hidden="true" /> Account
                </Link>
                <button type="button" onClick={onLogout} className="px-2 py-1.5 text-stone-600 hover:text-black">
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="px-2 py-1.5 hover:text-black">Log in</Link>
                <Link to="/register" className="px-2 py-1.5 hover:text-black">Register</Link>
              </>
            )}
            <Link to="/cart" className="relative inline-flex items-center gap-1 px-2 py-1.5 hover:text-black" aria-label={itemCount > 0 ? `Cart (${itemCount} item${itemCount === 1 ? '' : 's'})` : 'Cart'}>
              <ShoppingCart className="size-4" aria-hidden="true" />
              {itemCount > 0 && (
                <span className="inline-block rounded-full bg-black text-white text-[10px] leading-none px-1.5 py-0.5" aria-hidden="true">
                  {itemCount}
                </span>
              )}
            </Link>
          </nav>
        </div>

        {mobileMenuOpen && (
          <nav ref={mobileNavRef} className="sm:hidden border-t border-stone-200 bg-white px-4 py-3 flex flex-col gap-1 text-sm text-stone-700">
            <Link to="/browse" onClick={closeMenu} className="py-3 hover:text-black">Browse</Link>
            <Link to="/sell" onClick={closeMenu} className="py-3 hover:text-black">Sell</Link>
            <Link to="/about" onClick={closeMenu} className="py-3 hover:text-black">About</Link>
            {user ? (
              <>
                {user.sellerProfileExists ? (
                  <Link to="/seller/dashboard" onClick={closeMenu} className="py-3 hover:text-black">Seller dashboard</Link>
                ) : null}
                {user.role === 'ADMIN' ? (
                  <Link to="/admin/moderation" onClick={closeMenu} className="py-3 hover:text-black">Admin</Link>
                ) : null}
                <Link to="/account" onClick={closeMenu} className="py-3 hover:text-black inline-flex items-center gap-1">
                  <User className="size-4" aria-hidden="true" /> Account
                </Link>
                <button type="button" onClick={onLogout} className="py-3 text-left text-stone-600 hover:text-black">
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={closeMenu} className="py-3 hover:text-black">Log in</Link>
                <Link to="/register" onClick={closeMenu} className="py-3 hover:text-black">Register</Link>
              </>
            )}
          </nav>
        )}
      </header>

      <main id="main-content" className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-stone-200 bg-stone-50">
        <div className="mx-auto max-w-6xl px-4 py-6 text-sm text-stone-600 flex flex-wrap gap-3 justify-between">
          <span suppressHydrationWarning>&copy; {new Date().getFullYear()} {BRAND_NAME} &mdash; verified handmade goods, flat 5% fee</span>
          <nav aria-label="Footer" className="flex gap-1">
            <Link to="/about" className="px-2 py-3 hover:text-stone-900 hover:underline">About</Link>
            <Link to="/guides/handmade-jewelry" className="px-2 py-3 hover:text-stone-900 hover:underline">Guides</Link>
            <Link to="/policy/changelog" className="px-2 py-3 hover:text-stone-900 hover:underline">Policies</Link>
            <Link to="/sell" className="px-2 py-3 hover:text-stone-900 hover:underline">Sell</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
